import {ComponentProps, useCallback, useContext, useEffect, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';

import {Box, Grid, Switch, Typography} from '@mui/material';
import {alpha} from '@mui/material/styles';

import {BoardViewEmptyState} from './BoardViewEmptyState';
import {containerStyles} from './BoardViewStyles';
import {KanbanColumn} from './KanbanCard/KanbanColumn';
import {matchesBoard} from './MatchesBoard.colors';
import {CompanyContext} from '../../../../../../contexts/CompanyContext/CompanyContext';
import {SnackBarContext} from '../../../../../../contexts/SnackBarContext/SnackBarContext';
import {useApiWrite} from '../../../../../../hooks/useApiWrite';
import {BoardColumnData, BoardColumnKey} from '../../../../../../hooks/useMatchesBoardColumnsSorting';
import {Patch} from '../../../../../../lib/api/PATCH/Patch';
import {PatchRoutes} from '../../../../../../lib/api/PATCH/PatchRoutes';
import {AppConfig} from '../../../../../../lib/AppConfig';
import {buildMatchDetailsHrefFromMatchesBoard} from '../../../../../../lib/matchDetailsNav';
import {MatchStatus} from '../../../../../../types/MatchStatus/MatchStatus';
import {MatchData} from '../../../../../../types/shared-recruiting-types';
import {scheduleMatchBoardCardPulse} from '../../../../../../utils/matchBoardCardPulse';

interface Props {
  columns: Record<BoardColumnKey, BoardColumnData>;
  totalMatches: number;
  selectedFilter: string | null;
  onSuccess: () => void;
  /** Matches board: merge contacted + interview into one “In progress” column. */
  mergeInProgressColumns?: boolean;
  onMergeInProgressColumnsChange?: (merged: boolean) => void;
}

type KanbanProps = Pick<ComponentProps<typeof KanbanColumn>, 'header' | 'matches' | 'total'>;
type KanbanHeader = KanbanProps['header'];
const BOARD_PAGE_SIZE = AppConfig.PAGINATION_PER_PAGE;
const isBoardColumnKey = (header: KanbanHeader): header is BoardColumnKey => {
  return header === 'new' || header === 'contacted' || header === 'interview';
};

const STATUS_BY_HEADER: Partial<Record<KanbanHeader, MatchStatus>> = {
  new: MatchStatus.Accepted,
  contacted: MatchStatus.Contacted,
  interview: MatchStatus.Interview,
  hired: MatchStatus.Hired,
  onHold: MatchStatus.OnHold,
  rejected: MatchStatus.Rejected,
};

/** Same match can appear in multiple column feeds after navigation/refetch; keep first occurrence. */
const dedupeMatchesByResourceId = (matches: MatchData[]): MatchData[] => {
  const seen = new Set<string>();
  const out: MatchData[] = [];
  for (const m of matches) {
    const id = m.resourceId?.trim();
    if (!id || seen.has(id)) {
      continue;
    }
    seen.add(id);
    out.push(m);
  }
  return out;
};

export const BoardView = ({
  columns,
  totalMatches,
  selectedFilter,
  onSuccess,
  mergeInProgressColumns = false,
  onMergeInProgressColumnsChange,
}: Props) => {
  const navigate = useNavigate();
  const {search} = useLocation();
  const {t} = useTranslation();
  const {t: tInProgressLayout} = useTranslation('translation', {keyPrefix: 'matchesPage.inProgressLayout'});
  const {company} = useContext(CompanyContext);
  const companyId = company.data?.resourceId || '';
  const {dispatchMessage} = useContext(SnackBarContext);
  const [columnMatches, setColumnMatches] = useState<Record<BoardColumnKey, MatchData[]>>({
    new: [],
    contacted: [],
    interview: [],
  });
  const [visibleCountByHeader, setVisibleCountByHeader] = useState<Record<BoardColumnKey, number>>({
    new: BOARD_PAGE_SIZE,
    contacted: BOARD_PAGE_SIZE,
    interview: BOARD_PAGE_SIZE,
  });
  const [isLoadingMoreLocalByHeader, setIsLoadingMoreLocalByHeader] = useState<Record<BoardColumnKey, boolean>>({
    new: false,
    contacted: false,
    interview: false,
  });
  const [kanbanProps, setKanbanProps] = useState<KanbanProps[]>([]);
  const [visibleCountMerged, setVisibleCountMerged] = useState(BOARD_PAGE_SIZE);
  const [dragState, setDragState] = useState<{
    matchId: string;
    fromHeader: KanbanHeader;
  } | null>(null);
  const [dropTargetHeader, setDropTargetHeader] = useState<KanbanHeader | null>(null);
  const statusUpdateFailedRef = useRef(false);
  const pendingServerLoadByHeaderRef = useRef<Record<BoardColumnKey, boolean>>({
    new: false,
    contacted: false,
    interview: false,
  });
  const pendingInProgressMergedFetchRef = useRef(false);
  const mergeWasOffRef = useRef(true);

  const {makeManualRequestWithParams, isLoading: isStatusUpdating} = useApiWrite<Patch.ContactMatch>({
    method: 'patch',
    disableLoadingOverlay: true,
    onError: () => {
      statusUpdateFailedRef.current = true;
      dispatchMessage(t('companyApplicants.snackbars.error'), 'error');
    },
  });

  const statusPathByTarget = useCallback(
    (targetStatus: MatchStatus, matchId: string) => {
      switch (targetStatus) {
        case MatchStatus.Accepted:
          return PatchRoutes[Patch.ReAcceptMatchByCompany](companyId, matchId);
        case MatchStatus.Contacted:
          return PatchRoutes[Patch.ContactMatch](companyId, matchId);
        case MatchStatus.Interview:
          return PatchRoutes[Patch.InterviewMatch](companyId, matchId);
        case MatchStatus.Hired:
          return PatchRoutes[Patch.HireMatch](companyId, matchId);
        case MatchStatus.OnHold:
          return PatchRoutes[Patch.OnHoldMatch](companyId, matchId);
        default:
          return undefined;
      }
    },
    [companyId]
  );

  useEffect(() => {
    const incomingByHeader: Record<BoardColumnKey, MatchData[]> = {
      new: columns.new.matches,
      contacted: columns.contacted.matches,
      interview: columns.interview.matches,
    };
    const dedupedByHeader: Record<BoardColumnKey, MatchData[]> = {
      new: dedupeMatchesByResourceId(incomingByHeader.new),
      contacted: dedupeMatchesByResourceId(incomingByHeader.contacted),
      interview: dedupeMatchesByResourceId(incomingByHeader.interview),
    };

    setColumnMatches(dedupedByHeader);

    setVisibleCountByHeader((prev) => {
      const next = {...prev};
      (Object.keys(dedupedByHeader) as BoardColumnKey[]).forEach((header) => {
        const incomingLength = dedupedByHeader[header].length;
        if (pendingServerLoadByHeaderRef.current[header]) {
          next[header] = Math.min(prev[header] + BOARD_PAGE_SIZE, incomingLength);
          pendingServerLoadByHeaderRef.current[header] = false;
          return;
        }
        next[header] = Math.min(prev[header], Math.max(incomingLength, BOARD_PAGE_SIZE));
      });
      return next;
    });

    setIsLoadingMoreLocalByHeader((prev) => {
      const next = {...prev};
      (Object.keys(next) as BoardColumnKey[]).forEach((header) => {
        if (!columns[header].isLoadingMore) {
          next[header] = false;
        }
      });
      return next;
    });

    if (onMergeInProgressColumnsChange === undefined) {
      return;
    }

    const mergedLen = dedupeMatchesByResourceId([...dedupedByHeader.contacted, ...dedupedByHeader.interview]).length;
    if (pendingInProgressMergedFetchRef.current) {
      pendingInProgressMergedFetchRef.current = false;
      setVisibleCountMerged((prev) => Math.min(prev + BOARD_PAGE_SIZE, mergedLen));
      return;
    }
    setVisibleCountMerged((prev) => Math.min(prev, Math.max(mergedLen, BOARD_PAGE_SIZE)));
  }, [columns, onMergeInProgressColumnsChange]);

  const handleOpenMatchDetails = useCallback(
    (matchId: string) => {
      navigate(buildMatchDetailsHrefFromMatchesBoard(matchId, search));
    },
    [navigate, search]
  );

  const clearDragState = useCallback(() => {
    setDragState(null);
    setDropTargetHeader(null);
  }, []);

  const handleCardDragStart = useCallback((matchId: string, fromHeader: KanbanHeader) => {
    setDragState({matchId, fromHeader});
  }, []);

  const handleColumnDragOver = useCallback((targetHeader: KanbanHeader) => {
    setDropTargetHeader(targetHeader);
  }, []);

  const moveMatchToHeader = useCallback(
    async (matchId: string, targetHeader: KanbanHeader) => {
      if (isStatusUpdating) {
        return;
      }

      const draggedMatch =
        columnMatches.new.find((match) => match.resourceId === matchId) ||
        columnMatches.contacted.find((match) => match.resourceId === matchId) ||
        columnMatches.interview.find((match) => match.resourceId === matchId);
      if (!draggedMatch) {
        return;
      }

      const targetStatus = STATUS_BY_HEADER[targetHeader];
      if (!targetStatus || draggedMatch.status === targetStatus) {
        return;
      }

      const path = statusPathByTarget(targetStatus, draggedMatch.resourceId);
      if (!path) {
        return;
      }

      statusUpdateFailedRef.current = false;
      await makeManualRequestWithParams({
        method: 'patch',
        path,
      });

      if (statusUpdateFailedRef.current) {
        return;
      }

      setColumnMatches((prev) => {
        const updateColumn = (entries: MatchData[]) =>
          entries.filter((entry) => entry.resourceId !== draggedMatch.resourceId);

        const next: Record<BoardColumnKey, MatchData[]> = {
          new: updateColumn(prev.new),
          contacted: updateColumn(prev.contacted),
          interview: updateColumn(prev.interview),
        };
        const movedMatch = {...draggedMatch, status: targetStatus};
        if (targetHeader === 'new' || targetHeader === 'contacted' || targetHeader === 'interview') {
          next[targetHeader] = [movedMatch, ...next[targetHeader]];
        }
        return next;
      });
      onSuccess();
      scheduleMatchBoardCardPulse(draggedMatch.resourceId);
    },
    [columnMatches, isStatusUpdating, makeManualRequestWithParams, onSuccess, statusPathByTarget]
  );

  const handleCardDrop = useCallback(
    async (targetHeader: KanbanHeader) => {
      if (!dragState) {
        clearDragState();
        return;
      }

      if (targetHeader === 'inProgress') {
        const {matchId, fromHeader} = dragState;
        if (fromHeader === 'new') {
          await moveMatchToHeader(matchId, 'contacted');
        } else if (fromHeader === 'contacted') {
          await moveMatchToHeader(matchId, 'interview');
        }
        clearDragState();
        return;
      }

      await moveMatchToHeader(dragState.matchId, targetHeader as BoardColumnKey);
      clearDragState();
    },
    [clearDragState, dragState, moveMatchToHeader]
  );

  const resolveInProgressDragHeader = useCallback((match: MatchData): 'contacted' | 'interview' => {
    const status = match.status;
    if (status === MatchStatus.Interview || status === 'interview') {
      return 'interview';
    }
    return 'contacted';
  }, []);

  const handleMoveToContacted = useCallback(
    async (matchId: string) => {
      await moveMatchToHeader(matchId, 'contacted');
    },
    [moveMatchToHeader]
  );

  const handleMoveToRejected = useCallback(
    async (matchId: string) => {
      setColumnMatches((prev) => ({
        new: prev.new.filter((entry) => entry.resourceId !== matchId),
        contacted: prev.contacted.filter((entry) => entry.resourceId !== matchId),
        interview: prev.interview.filter((entry) => entry.resourceId !== matchId),
      }));
      onSuccess();
    },
    [onSuccess]
  );

  const handleLoadMore = useCallback(
    (header: BoardColumnKey) => {
      const visibleCount = visibleCountByHeader[header];
      const loadedCount = columnMatches[header].length;
      const columnConfig = columns[header];
      const isLoading = columnConfig.isLoadingMore || isLoadingMoreLocalByHeader[header];

      if (isLoading) {
        return;
      }

      if (visibleCount < loadedCount) {
        setVisibleCountByHeader((prev) => ({
          ...prev,
          [header]: Math.min(prev[header] + BOARD_PAGE_SIZE, loadedCount),
        }));
        return;
      }

      if (columnConfig.hasNext) {
        pendingServerLoadByHeaderRef.current[header] = true;
        setIsLoadingMoreLocalByHeader((prev) => ({...prev, [header]: true}));
        columnConfig.getNext();
      }
    },
    [columnMatches, columns, isLoadingMoreLocalByHeader, visibleCountByHeader]
  );

  const handleLoadMoreMerged = useCallback(() => {
    const mergedLen = dedupeMatchesByResourceId([...columnMatches.contacted, ...columnMatches.interview]).length;
    const isContactedLoading = columns.contacted.isLoadingMore || isLoadingMoreLocalByHeader.contacted;
    const isInterviewLoading = columns.interview.isLoadingMore || isLoadingMoreLocalByHeader.interview;
    if (isContactedLoading || isInterviewLoading) {
      return;
    }

    if (visibleCountMerged < mergedLen) {
      setVisibleCountMerged((prev) => Math.min(prev + BOARD_PAGE_SIZE, mergedLen));
      return;
    }

    if (columns.contacted.hasNext) {
      pendingInProgressMergedFetchRef.current = true;
      setIsLoadingMoreLocalByHeader((prev) => ({...prev, contacted: true}));
      columns.contacted.getNext();
      return;
    }

    if (columns.interview.hasNext) {
      pendingInProgressMergedFetchRef.current = true;
      setIsLoadingMoreLocalByHeader((prev) => ({...prev, interview: true}));
      columns.interview.getNext();
    }
  }, [
    columnMatches.contacted,
    columnMatches.interview,
    columns.contacted,
    columns.interview,
    isLoadingMoreLocalByHeader.contacted,
    isLoadingMoreLocalByHeader.interview,
    visibleCountMerged,
  ]);

  useEffect(() => {
    if (!onMergeInProgressColumnsChange) {
      mergeWasOffRef.current = true;
      return;
    }
    if (mergeInProgressColumns && mergeWasOffRef.current) {
      mergeWasOffRef.current = false;
      const cap = columnMatches.contacted.length + columnMatches.interview.length;
      const approx =
        Math.min(visibleCountByHeader.contacted, columnMatches.contacted.length) +
        Math.min(visibleCountByHeader.interview, columnMatches.interview.length);
      setVisibleCountMerged(
        Math.min(Math.max(approx > 0 ? approx : BOARD_PAGE_SIZE, BOARD_PAGE_SIZE), Math.max(cap, BOARD_PAGE_SIZE))
      );
    }
    if (!mergeInProgressColumns) {
      mergeWasOffRef.current = true;
    }
  }, [
    mergeInProgressColumns,
    onMergeInProgressColumnsChange,
    columnMatches.contacted.length,
    columnMatches.interview.length,
    visibleCountByHeader.contacted,
    visibleCountByHeader.interview,
  ]);

  useEffect(() => {
    const visibleMatchesByHeader: Record<BoardColumnKey, MatchData[]> = {
      new: columnMatches.new.slice(0, visibleCountByHeader.new),
      contacted: columnMatches.contacted.slice(0, visibleCountByHeader.contacted),
      interview: columnMatches.interview.slice(0, visibleCountByHeader.interview),
    };

    const filterBy = (status: MatchStatus) => {
      return visibleMatchesByHeader.new
        .concat(visibleMatchesByHeader.contacted)
        .concat(visibleMatchesByHeader.interview)
        .filter((match) => match.status === status);
    };

    if (selectedFilter === MatchStatus.Hired) {
      const hiredMatches = filterBy(MatchStatus.Hired);
      return setKanbanProps([{header: 'hired', total: hiredMatches.length, matches: hiredMatches}]);
    }

    if (selectedFilter === MatchStatus.OnHold) {
      const onHold = filterBy(MatchStatus.OnHold);

      return setKanbanProps([{header: 'onHold', total: onHold.length, matches: onHold}]);
    }

    if (selectedFilter === MatchStatus.Rejected) {
      const rejected = filterBy(MatchStatus.Rejected);
      return setKanbanProps([{header: 'rejected', total: rejected.length, matches: rejected}]);
    }

    setKanbanProps([
      {
        header: 'new',
        total: columnMatches.new.length,
        matches: visibleMatchesByHeader.new,
      },
      {
        header: 'contacted',
        total: columnMatches.contacted.length,
        matches: visibleMatchesByHeader.contacted,
      },
      {
        header: 'interview',
        total: columnMatches.interview.length,
        matches: visibleMatchesByHeader.interview,
      },
    ]);
  }, [columnMatches, selectedFilter, visibleCountByHeader]);

  if (totalMatches === 0) {
    return <BoardViewEmptyState />;
  }

  const showMatchesInProgressLayout =
    Boolean(onMergeInProgressColumnsChange) &&
    selectedFilter === null &&
    kanbanProps.length === 3 &&
    kanbanProps[0]?.header === 'new' &&
    kanbanProps[1]?.header === 'contacted' &&
    kanbanProps[2]?.header === 'interview';

  const mergedAllMatches = dedupeMatchesByResourceId([...columnMatches.contacted, ...columnMatches.interview]);
  const mergedVisibleMatches = mergedAllMatches.slice(0, visibleCountMerged);
  const mergedTotal = mergedAllMatches.length;
  const mergedCanLoadMore = visibleCountMerged < mergedTotal || columns.contacted.hasNext || columns.interview.hasNext;
  const mergedLoadingMore =
    columns.contacted.isLoadingMore ||
    columns.interview.isLoadingMore ||
    isLoadingMoreLocalByHeader.contacted ||
    isLoadingMoreLocalByHeader.interview;

  const renderKanbanColumn = (kanban: KanbanProps | undefined, options?: {flexChild?: boolean}) => {
    if (!kanban) {
      return null;
    }
    if (!isBoardColumnKey(kanban.header)) {
      return (
        <KanbanColumn
          key={kanban.header}
          flexChild={options?.flexChild}
          header={kanban.header}
          matches={kanban.matches}
          total={kanban.total}
          onOpenMatchDetails={handleOpenMatchDetails}
          onCardDragStart={handleCardDragStart}
          onCardDragEnd={clearDragState}
          onColumnDragOver={handleColumnDragOver}
          onCardDrop={handleCardDrop}
          isDropTarget={dropTargetHeader === kanban.header && dragState?.fromHeader !== kanban.header}
          onMoveToContacted={handleMoveToContacted}
          onMoveToRejected={handleMoveToRejected}
          isStatusUpdating={isStatusUpdating}
          canLoadMore={false}
          onLoadMore={undefined}
          isLoadingMore={false}
        />
      );
    }
    const header = kanban.header;
    const canLoadMore = visibleCountByHeader[header] < columnMatches[header].length || columns[header].hasNext;
    const onLoadMore = () => handleLoadMore(header);
    const isLoadingMore = columns[header].isLoadingMore || isLoadingMoreLocalByHeader[header];

    return (
      <KanbanColumn
        key={header}
        flexChild={options?.flexChild}
        header={header}
        matches={kanban.matches}
        total={kanban.total}
        onOpenMatchDetails={handleOpenMatchDetails}
        onCardDragStart={handleCardDragStart}
        onCardDragEnd={clearDragState}
        onColumnDragOver={handleColumnDragOver}
        onCardDrop={handleCardDrop}
        isDropTarget={dropTargetHeader === header && dragState?.fromHeader !== header}
        onMoveToContacted={handleMoveToContacted}
        onMoveToRejected={handleMoveToRejected}
        isStatusUpdating={isStatusUpdating}
        canLoadMore={canLoadMore}
        onLoadMore={onLoadMore}
        isLoadingMore={isLoadingMore}
      />
    );
  };

  const boardGrid = (
    <Grid container spacing={2} sx={containerStyles}>
      {kanbanProps.map((kanban: KanbanProps) => renderKanbanColumn(kanban))}
    </Grid>
  );

  const matchesBoardColumnsRowSx = {
    display: 'flex',
    flexDirection: {xs: 'column', md: 'row'} as const,
    gap: 2,
    alignItems: 'flex-start',
    width: '100%',
    '& > *': {
      flex: {md: '1 1 0'},
      minWidth: 0,
      width: {xs: '100%', md: 'auto'},
    },
  };

  const mergeToggleActive = matchesBoard.layoutToggleActive;
  const mergeToggleTrack = matchesBoard.layoutToggleTrack;

  const matchesSplitMergeGrid = showMatchesInProgressLayout ? (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box
          data-tour='matches.boardLayoutToggle'
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            gap: 1,
            mb: 1,
          }}
        >
          <Typography
            component='span'
            id='matches-in-progress-split-label'
            variant='body2'
            sx={{
              fontWeight: mergeInProgressColumns ? 400 : 600,
              color: mergeInProgressColumns ? 'text.secondary' : mergeToggleActive,
            }}
          >
            {tInProgressLayout('splitByStatus')}
          </Typography>
          <Switch
            size='small'
            checked={mergeInProgressColumns}
            onChange={(_, checked) => onMergeInProgressColumnsChange?.(checked)}
            inputProps={{
              'aria-labelledby': 'matches-in-progress-split-label matches-in-progress-single-label',
            }}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: mergeToggleActive,
                '&:hover': {
                  backgroundColor: alpha(mergeToggleActive, 0.08),
                },
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: mergeToggleTrack,
                opacity: 1,
              },
            }}
          />
          <Typography
            component='span'
            id='matches-in-progress-single-label'
            variant='body2'
            sx={{
              fontWeight: mergeInProgressColumns ? 600 : 400,
              color: mergeInProgressColumns ? mergeToggleActive : 'text.secondary',
            }}
          >
            {tInProgressLayout('singleColumn')}
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Box sx={matchesBoardColumnsRowSx}>
          {renderKanbanColumn(kanbanProps[0], {flexChild: true})}
          {mergeInProgressColumns ? (
            <KanbanColumn
              key='inProgress'
              flexChild
              header='inProgress'
              matches={mergedVisibleMatches}
              total={mergedTotal}
              onOpenMatchDetails={handleOpenMatchDetails}
              onCardDragStart={handleCardDragStart}
              onCardDragEnd={clearDragState}
              onColumnDragOver={handleColumnDragOver}
              onCardDrop={handleCardDrop}
              resolveDragSourceHeader={resolveInProgressDragHeader}
              isDropTarget={dropTargetHeader === 'inProgress' && dragState?.fromHeader !== 'inProgress'}
              onMoveToContacted={undefined}
              onMoveToRejected={handleMoveToRejected}
              isStatusUpdating={isStatusUpdating}
              canLoadMore={mergedCanLoadMore}
              onLoadMore={handleLoadMoreMerged}
              isLoadingMore={mergedLoadingMore}
            />
          ) : (
            <>
              {renderKanbanColumn(kanbanProps[1], {flexChild: true})}
              {renderKanbanColumn(kanbanProps[2], {flexChild: true})}
            </>
          )}
        </Box>
      </Grid>
    </Grid>
  ) : null;

  return matchesSplitMergeGrid ?? boardGrid;
};
